import { createFileRoute, redirect } from "@tanstack/react-router";
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { DashboardLayout } from "@/components/ui/dashboard-layout";
import {
  RefreshCw,
  Clipboard,
  Check,
  UserCircle,
  BarChart2,
  Mail,
  Clock,
  Filter,
  AlertCircle,
  Search,
  X,
} from "lucide-react";
import { useQuery, useMutation, useInfiniteQuery } from "@tanstack/react-query";
import { emailsApi } from "@/lib/api";
import subscriptionApi from "@/lib/subscriptionApi";
import type { EmailUI, EmailStats, SourceType } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import Markdown from "react-markdown";
export const Route = createFileRoute("/dashboard/")({
  component: Dashboard,
  head: () => {
    return {
      meta: [
        {
          title: "Dashboard | aiEmailAgent",
        },
        {
          description: "Dashboard",
        },
        {
          nofollow: true,
        },
        {
          noindex: true,
        },
      ],
    };
  },
  beforeLoad: async ({ context }) => {
    // Get the auth user state from the context
    const user = context.auth.user;
    // If onboarding is not complete, redirect to onboarding
    if (user && user.onboardingComplete === false) {
      throw redirect({
        to: "/onboarding",
      });
    }

    return {};
  },
});

// Helper function to format the date
function formatEmailDate(date: Date): string {
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);

  if (
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear()
  ) {
    return new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } else if (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  ) {
    return "Yesterday";
  } else {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
    }).format(date);
  }
}

// Helper function to get source type badge style
function getSourceTypeBadgeStyle(sourceType?: string): {
  bg: string;
  text: string;
} {
  switch (sourceType) {
    case "salesforge":
      return { bg: "bg-blue-100", text: "text-blue-800" };
    case "instantly":
      return { bg: "bg-purple-100", text: "text-purple-800" };
    default:
      return { bg: "bg-gray-100", text: "text-gray-800" };
  }
}

function Dashboard() {
  const [selectedEmail, setSelectedEmail] = useState<EmailUI | null>(null);
  const [copied, setCopied] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<SourceType | undefined>();
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);
  const [showOriginalEmail, setShowOriginalEmail] = useState(false);
  const ITEMS_PER_PAGE = 20;
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch emails using React Query with infinite scroll
  const emailsQuery = useInfiniteQuery({
    queryKey: ["emails", sourceFilter, statusFilter, debouncedSearchQuery],
    queryFn: async ({ pageParam = 0 }) => {
      const data = await emailsApi.getEmails(sourceFilter, {
        limit: ITEMS_PER_PAGE,
        offset: pageParam,
        status: statusFilter,
        search: debouncedSearchQuery,
      });
      return {
        emails: emailsApi.transformEmailsForUI(data.emails),
        pagination: data.pagination,
      };
    },
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.pagination.offset + ITEMS_PER_PAGE;
      return nextOffset < lastPage.pagination.total ? nextOffset : undefined;
    },
    initialPageParam: 0,
  });

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          emailsQuery.hasNextPage &&
          !emailsQuery.isFetching
        ) {
          emailsQuery.fetchNextPage();
        }
      },
      { threshold: 0.5 },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [emailsQuery.hasNextPage, emailsQuery.isFetching]);

  // Fetch email stats using React Query
  const statsQuery = useQuery<EmailStats, Error>({
    queryKey: ["emailStats"],
    queryFn: async () => {
      const stats = await emailsApi.getEmailStats();
      return stats;
    },
  });

  // Mutation for refreshing emails
  const refreshMutation = useMutation({
    mutationFn: async () => {
      const data = await emailsApi.getEmails();
      return emailsApi.transformEmailsForUI(data.emails);
    },
    onSuccess: () => {
      // Update the emails query cache with new data
      emailsQuery.refetch();
      statsQuery.refetch();
    },
  });

  // Mutation for marking an email as handled
  const markAsHandledMutation = useMutation({
    mutationFn: (emailId: string) => emailsApi.markAsHandled(emailId),
    onSuccess: () => {
      // Refetch emails to update the list
      emailsQuery.refetch();
      statsQuery.refetch();
      // Show a success notification (could use a toast here)
    },
  });

  // Add subscription query
  const subscriptionQuery = useQuery({
    queryKey: ["subscription"],
    queryFn: async () => {
      const response = await subscriptionApi.getCurrentSubscription();
      return response.subscription;
    },
  });

  // Add subscription management mutation
  const managePaymentMutation = useMutation({
    mutationFn: () => subscriptionApi.createPortalSession(window.location.href),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  // Check if subscription needs attention
  const needsPaywall = (status?: string) => {
    return ["past_due", "unpaid", "incomplete", "incomplete_expired"].includes(
      status || "",
    );
  };

  const handleRefresh = () => {
    refreshMutation.mutate();
  };

  const handleMarkAsHandled = () => {
    if (selectedEmail) {
      markAsHandledMutation.mutate(String(selectedEmail.id));
    }
  };

  const handleCopy = (text: string) => {
    // Copy text to clipboard
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Dashboard statistics based on data or loading state
  const responseRate = statsQuery.data
    ? Math.round(
        (statsQuery.data.stats[0]?.processedEmails /
          (statsQuery.data.stats[0]?.totalEmails || 1)) *
          100,
      )
    : 0;
  const timeSaved = statsQuery.data
    ? statsQuery.data.stats[0]?.processedEmails * 0.25
    : 0;
  const stats = [
    {
      label: "Emails Processed",
      value: statsQuery.data
        ? statsQuery.data.stats[0]?.processedEmails || 0
        : "—",
      icon: <Mail className="h-5 w-5" />,
      color: "bg-blue-50 text-blue-600",
      isLoading: statsQuery.isLoading,
    },
    {
      label: "Response Rate",
      value: isNaN(responseRate) ? "—" : `${responseRate}%`,
      icon: <BarChart2 className="h-5 w-5" />,
      color: "bg-green-50 text-green-600",
      isLoading: statsQuery.isLoading,
    },
    {
      label: "Time Saved",
      value:
        timeSaved === 0 || isNaN(timeSaved)
          ? "—"
          : `${timeSaved.toFixed(1)} hrs`,
      icon: <Clock className="h-5 w-5" />,
      color: "bg-purple-50 text-purple-600",
      isLoading: statsQuery.isLoading,
    },
  ];
  return (
    <DashboardLayout title="Dashboard" activePath="/dashboard">
      {/* Subscription Paywall Overlay */}
      {subscriptionQuery.data &&
        needsPaywall(subscriptionQuery.data.status) && (
          <div className="fixed inset-0 z-50 bg-white/80">
            <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-lg bg-white shadow-lg border border-gray-200 rounded-lg p-6">
              <div className="text-center">
                <h2 className="text-xl font-bold mb-2">
                  Subscription Required
                </h2>
                <p className="text-gray-600 mb-6">
                  Your subscription needs attention. Please update your payment
                  information to continue using our AI features.
                </p>
              </div>
              <div className="flex justify-center">
                <Button
                  className="w-full sm:w-auto"
                  onClick={() => managePaymentMutation.mutate()}
                  disabled={managePaymentMutation.isPending}
                >
                  {managePaymentMutation.isPending ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Please wait...
                    </>
                  ) : (
                    "Update Payment"
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

      <div className="p-4 sm:p-6 space-y-6 h-full">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="p-4 sm:p-6 rounded-xl border bg-card shadow-sm hover:shadow-md transition-shadow duration-200"
            >
              <div className="flex items-center space-x-4">
                <div
                  className={`h-10 w-10 sm:h-12 sm:w-12 rounded-full ${stat.color} bg-opacity-20 flex items-center justify-center`}
                >
                  {stat.icon}
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {stat.label}
                  </p>
                  {stat.isLoading ? (
                    <div className="h-8 w-16 bg-muted animate-pulse rounded-md"></div>
                  ) : (
                    <h3 className="text-xl sm:text-2xl font-bold">
                      {stat.value}
                    </h3>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col md:flex-row gap-6 h-[calc(100vh-16rem)]">
          {/* Email List */}
          <div className="w-full md:w-1/3 overflow-hidden rounded-xl border bg-card shadow-sm flex flex-col max-h-[50vh] md:max-h-none">
            <div className="p-4 border-b flex-shrink-0">
              {/* Header section - stack on mobile, row on desktop */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h3 className="font-medium">Recent Emails</h3>

                {/* Search and actions container */}
                <div className="flex flex-1 gap-3 sm:max-w-[600px]">
                  {/* Search box */}
                  <div className="relative flex-1">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder="Search emails..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-8 w-full"
                    />
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {(searchQuery || sourceFilter || statusFilter) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSearchQuery("");
                          setSourceFilter(undefined);
                          setStatusFilter(undefined);
                          setShowFilters(false);
                        }}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        emailsQuery.refetch();
                        statsQuery.refetch();
                      }}
                      disabled={emailsQuery.isFetching}
                      className="flex items-center"
                    >
                      <RefreshCw
                        className={`h-4 w-4 ${emailsQuery.isFetching ? "animate-spin" : ""}`}
                      />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowFilters((prev: boolean) => !prev)}
                    >
                      <Filter className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Filters */}
              {showFilters && (
                <div className="mt-4 space-y-2">
                  <Select
                    value={sourceFilter}
                    onValueChange={(value: string) =>
                      setSourceFilter(
                        value === "undefined"
                          ? undefined
                          : (value as SourceType),
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="undefined">All Sources</SelectItem>
                      <SelectItem value="salesforge">Salesforge</SelectItem>
                      <SelectItem value="instantly">Instantly</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={statusFilter}
                    onValueChange={(value: string) =>
                      setStatusFilter(value === "undefined" ? undefined : value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="undefined">All Statuses</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="processing">Processing</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="error">Error</SelectItem>
                      <SelectItem value="insufficient_credits">
                        Insufficient Credits
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="overflow-y-auto flex-1">
              {emailsQuery.isLoading ? (
                <div className="p-4 space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="animate-pulse space-y-3 p-3 border rounded-lg"
                    >
                      <div className="flex space-x-3 items-center">
                        <div className="h-8 w-8 rounded-full bg-muted"></div>
                        <div className="space-y-2 flex-1">
                          <div className="h-4 bg-muted rounded w-1/3"></div>
                          <div className="h-3 bg-muted rounded w-full"></div>
                        </div>
                      </div>
                      <div className="h-6 w-24 bg-muted rounded-full"></div>
                    </div>
                  ))}
                </div>
              ) : emailsQuery.data?.pages[0].emails.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-muted-foreground">
                    No emails found. Check your filters or connection settings.
                  </p>
                </div>
              ) : (
                <>
                  <div className="p-2 space-y-2">
                    {emailsQuery.data?.pages.map((page, i) => (
                      <React.Fragment key={i}>
                        {page.emails.map((email) => (
                          <div
                            key={email.id}
                            className={`cursor-pointer rounded-lg p-3 transition-all duration-200 hover:bg-accent/50 ${
                              selectedEmail?.id === email.id
                                ? "bg-primary/10 border-l-4 border-primary"
                                : "border"
                            }`}
                            onClick={() => setSelectedEmail(email)}
                          >
                            <div className="flex items-start space-x-3">
                              <div className="mt-1 h-8 w-8 flex-shrink-0 rounded-full bg-blue-100 flex items-center justify-center text-blue-800 text-xs font-medium border border-blue-200">
                                {email.senderName &&
                                  email.senderName
                                    .split(" ")
                                    .map((part) => part[0])
                                    .join("")}
                              </div>
                              <div className="flex-1 min-w-0 space-y-1">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="font-medium text-sm truncate flex-1">
                                    {email.senderName || email.sender}
                                  </span>
                                  <span className="text-xs text-muted-foreground flex-shrink-0">
                                    {formatEmailDate(
                                      new Date(email.receivedAt),
                                    )}
                                  </span>
                                </div>
                                <p className="text-sm text-foreground clamp-2 leading-tight">
                                  {email.subject}
                                </p>
                                <div className="mt-1 flex flex-wrap items-center gap-2">
                                  <span
                                    className={`${
                                      getSourceTypeBadgeStyle(email.sourceType)
                                        .bg
                                    } ${
                                      getSourceTypeBadgeStyle(email.sourceType)
                                        .text
                                    } text-xs px-2 py-0.5 rounded-full font-medium`}
                                  >
                                    {email.sourceLabel}
                                  </span>
                                  {email.processingStatus === "completed" ? (
                                    <span
                                      className={`hidden lg:inline rounded-full px-2 py-0.5 text-xs font-medium ${
                                        email.confidence === "high"
                                          ? "bg-green-100 text-green-800"
                                          : email.confidence === "medium"
                                            ? "bg-yellow-100 text-yellow-800"
                                            : "bg-red-100 text-red-800"
                                      }`}
                                    >
                                      {email.confidence === "high"
                                        ? "High"
                                        : email.confidence === "medium"
                                          ? "Medium"
                                          : "Low"}{" "}
                                      Confidence
                                    </span>
                                  ) : email.processingStatus ===
                                    "insufficient_credits" ? (
                                    <span className="rounded-full bg-red-50 text-red-600 px-2 py-0.5 text-xs font-medium flex items-center">
                                      Insufficient Credits
                                    </span>
                                  ) : (
                                    <span className="rounded-full bg-blue-50 text-blue-600 px-2 py-0.5 text-xs font-medium flex items-center">
                                      <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                      Processing
                                    </span>
                                  )}
                                  {email.handled && (
                                    <span className="rounded-full bg-green-50 text-green-700 px-2 py-0.5 text-xs font-medium flex items-center">
                                      <Check className="h-3 w-3 mr-1" />
                                      Handled
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </React.Fragment>
                    ))}
                  </div>
                  {/* Infinite scroll trigger */}
                  <div ref={loadMoreRef} className="p-4 text-center">
                    {emailsQuery.isFetchingNextPage ? (
                      <div className="flex items-center justify-center">
                        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                        Loading more...
                      </div>
                    ) : emailsQuery.hasNextPage ? (
                      "Load more"
                    ) : (
                      "No more emails"
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Email Detail & Suggestion */}
          <div className="flex-1 overflow-hidden rounded-xl border bg-card shadow-sm flex flex-col">
            {selectedEmail ? (
              <div className="flex flex-col h-full">
                <div className="p-4 sm:p-6 border-b flex-shrink-0">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                    <div className="space-y-1 sm:space-y-2">
                      <h3 className="text-lg sm:text-xl font-semibold break-words">
                        {selectedEmail.subject}
                      </h3>
                      <div className="flex flex-col sm:flex-row sm:items-center text-sm text-muted-foreground gap-1 sm:gap-2">
                        <div className="flex items-center gap-2 truncate">
                          <span className="truncate">
                            From:{" "}
                            {selectedEmail.senderName || selectedEmail.sender}
                          </span>
                          <span className="hidden sm:inline text-muted-foreground/50">
                            •
                          </span>
                          <span className="hidden sm:inline text-muted-foreground/75 truncate">
                            {selectedEmail.sender}
                          </span>
                        </div>
                        <span className="hidden sm:inline text-muted-foreground/50">
                          •
                        </span>
                        <span className="text-xs sm:text-sm">
                          {new Date(selectedEmail.receivedAt).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div
                      className={`self-start rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap ${
                        selectedEmail.confidence === "high"
                          ? "bg-green-100 text-green-800"
                          : selectedEmail.confidence === "medium"
                            ? "bg-yellow-100 text-yellow-800"
                            : selectedEmail.confidence === "low"
                              ? "bg-red-100 text-red-800"
                              : "bg-blue-50 text-blue-600"
                      }`}
                    >
                      {selectedEmail.confidence === "high"
                        ? "High Confidence"
                        : selectedEmail.confidence === "medium"
                          ? "Medium Confidence"
                          : selectedEmail.confidence === "low"
                            ? "Low Confidence"
                            : "Processing"}
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                  <div className="p-4 sm:p-6 space-y-6">
                    {/* Original Message - Collapsible */}
                    <div className="rounded-lg border bg-background">
                      <button
                        onClick={() => setShowOriginalEmail(!showOriginalEmail)}
                        className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center">
                          <UserCircle className="h-5 w-5 mr-2 text-blue-500" />
                          <h4 className="font-medium">Original Message</h4>
                        </div>
                        <svg
                          className={`h-5 w-5 transform transition-transform ${
                            showOriginalEmail ? "rotate-180" : ""
                          }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                      {showOriginalEmail && (
                        <div className="px-4 pb-4">
                          <div className="text-sm text-muted-foreground whitespace-pre-line">
                            {selectedEmail?.body}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* AI Response Section - With Subscription Check */}
                    {selectedEmail?.hasResponse && selectedEmail?.aiResponse ? (
                      subscriptionQuery.data?.status === "active" ? (
                        <div className="rounded-lg border bg-primary/5 p-4">
                          <h4 className="mb-3 font-medium flex items-center text-primary">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className="mr-2"
                            >
                              <path
                                d="M10 2L3 5.5L10 9L17 5.5L10 2Z"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M3 14.5L10 18L17 14.5"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M3 10L10 13.5L17 10"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            AI Response Suggestion
                          </h4>
                          <div className="bg-white rounded-lg p-4 border shadow-sm ai-response">
                            <Markdown>{selectedEmail.aiResponse}</Markdown>
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-lg border bg-muted/30 p-8 text-center">
                          <AlertCircle className="h-8 w-8 mx-auto mb-4 text-yellow-500" />
                          <h4 className="font-medium">Subscription Required</h4>
                          <p className="text-sm text-muted-foreground mt-2">
                            Please update your subscription to view AI-generated
                            responses.
                          </p>
                          <Button
                            variant="default"
                            className="mt-4"
                            onClick={() => managePaymentMutation.mutate()}
                            disabled={managePaymentMutation.isPending}
                          >
                            Update Subscription
                          </Button>
                        </div>
                      )
                    ) : selectedEmail?.processingStatus ===
                      "insufficient_credits" ? (
                      <div className="rounded-lg border bg-muted/30 p-8 text-center">
                        <h4 className="font-medium">Insufficient Credits</h4>
                        <p className="text-sm text-muted-foreground mt-2">
                          Please upgrade your plan to continue using our AI.
                        </p>
                        <Button
                          variant="default"
                          className="mt-4"
                          onClick={() => managePaymentMutation.mutate()}
                          disabled={managePaymentMutation.isPending}
                        >
                          Upgrade Plan
                        </Button>
                      </div>
                    ) : (
                      <div className="rounded-lg border bg-muted/30 p-8 text-center">
                        <RefreshCw className="h-8 w-8 mx-auto mb-4 text-muted-foreground animate-spin" />
                        <h4 className="font-medium">Generating Response</h4>
                        <p className="text-sm text-muted-foreground mt-2">
                          Our AI is currently processing this email. Please
                          check back later.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons - With Subscription Check */}
                <div className="p-4 sm:p-6 border-t flex flex-col sm:flex-row gap-3 flex-shrink-0">
                  {subscriptionQuery.data?.status === "active" ? (
                    <>
                      <Button
                        onClick={() =>
                          selectedEmail.aiResponse &&
                          handleCopy(selectedEmail.aiResponse)
                        }
                        className={`w-full sm:flex-1 ${copied ? "bg-green-600" : ""} transition-colors duration-300`}
                      >
                        {copied ? (
                          <span className="flex items-center justify-center">
                            <Check className="h-4 w-4 mr-2" />
                            Copied!
                          </span>
                        ) : (
                          <span className="flex items-center justify-center">
                            <Clipboard className="h-4 w-4 mr-2" />
                            Copy to Clipboard
                          </span>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full sm:flex-1"
                        onClick={handleMarkAsHandled}
                        disabled={
                          markAsHandledMutation.isPending ||
                          selectedEmail?.handled === true
                        }
                      >
                        {markAsHandledMutation.isPending ? (
                          <span className="flex items-center">
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            Processing...
                          </span>
                        ) : selectedEmail?.handled ? (
                          <span className="flex items-center">
                            <Check className="h-4 w-4 mr-2" />
                            Handled
                          </span>
                        ) : (
                          <span className="flex items-center">
                            <Check className="h-4 w-4 mr-2" />
                            Mark as Handled
                          </span>
                        )}
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="default"
                      fullWidth
                      onClick={() => managePaymentMutation.mutate()}
                      disabled={managePaymentMutation.isPending}
                    >
                      Update Subscription to Access Features
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center p-4 sm:p-8">
                <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <Mail className="h-8 w-8 sm:h-10 sm:w-10 text-primary opacity-70" />
                </div>
                <h3 className="text-lg sm:text-xl font-medium">
                  Select an email
                </h3>
                <p className="mt-2 text-sm text-muted-foreground max-w-sm">
                  Choose an email from the list to view the original message and
                  AI-generated reply suggestions
                </p>
                <Button
                  variant="outline"
                  className="mt-6"
                  onClick={handleRefresh}
                  disabled={refreshMutation.isPending || emailsQuery.isLoading}
                >
                  <RefreshCw
                    className={`h-4 w-4 mr-2 ${refreshMutation.isPending ? "animate-spin" : ""}`}
                  />
                  Refresh Emails
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
